import React, { useState } from 'react';
import Layout from '../../components/Layout';
import apiService from '../../services/apiService';
import API_BASE_URL from '../../config/api';
import { useAdmin } from '../../context/AdminContext';
import { useAlert } from '../../context/AlertContext';
import { useConfirm } from '../../context/ConfirmContext';
import AutoRefreshIndicator from '../../components/AutoRefreshIndicator';
import {
    Search,
    Filter,
    Shield,
    UserCheck,
    UserX,
    Download,
    Eye,
    Edit,
    Trash2,
    CheckCircle,
    XCircle,
    Lock,
    X,
    FileText,
    TrendingUp,
    RefreshCw,
    Calendar,
    Users as UsersIcon
} from 'lucide-react';

import useLockBodyScroll from '../../utils/useLockBodyScroll';

const UserModal = ({ isOpen, onClose, mode, user, onSave }) => {
    useLockBodyScroll(isOpen);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        role: 'Distributor',
        rank: 'None',
        status: 'Active'
    });

    React.useEffect(() => {
        if (user && mode === 'edit') {
            setFormData({
                name: user.name || '',
                email: user.email || '',
                role: user.role || 'Distributor',
                rank: user.rank || 'None',
                status: user.status || 'Active'
            });
        } else {
            setFormData({
                name: '',
                email: '',
                role: 'Distributor',
                rank: 'None',
                status: 'Active'
            });
        }
    }, [user, mode]);

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl transform transition-all scale-100 opacity-100">
                <div className="flex justify-between items-center p-6 border-b border-gray-100">
                    <h3 className="text-xl font-bold text-gray-900">
                        {mode === 'add' ? 'Add New User' : 'Edit User'}
                    </h3>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full text-gray-500 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                        <input
                            type="text"
                            required
                            className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                            value={formData.name}
                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                            placeholder="Enter full name"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                        <input
                            type="email"
                            required
                            className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                            value={formData.email}
                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                            placeholder="Enter email address"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                            <select
                                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                                value={formData.role}
                                onChange={e => setFormData({ ...formData, role: e.target.value })}
                            >
                                <option value="Distributor">Distributor</option>
                                <option value="Franchise">Franchise</option>
                                <option value="Admin">Admin</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Rank</label>
                            <select
                                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                                value={formData.rank}
                                onChange={e => setFormData({ ...formData, rank: e.target.value })}
                            >
                                <option value="None">None</option>
                                <option value="Bronze">Bronze</option>
                                <option value="Silver">Silver</option>
                                <option value="Gold">Gold</option>
                                <option value="Diamond">Diamond</option>
                                <option value="Platinum">Platinum</option>
                            </select>
                        </div>
                    </div>

                    <div className="pt-4 flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all"
                        >
                            {mode === 'add' ? 'Create User' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const ViewUserModal = ({ isOpen, onClose, user, onUpdateCF }) => {
    useLockBodyScroll(isOpen);
    if (!isOpen || !user) return null;

    const sections = [
        { label: 'Personal Info', icon: UserCheck, fields: ['name', 'email', 'mobile', 'date_of_birth', 'pan_number'] },
        { label: 'Address', icon: UserCheck, fields: ['full_address', 'city', 'state', 'country', 'pin_code'] },
        { label: 'Network', icon: Shield, fields: ['member_id', 'created_at', 'sponsor_id', 'sponsor_name', 'upline_id', 'role', 'rank', 'status', 'selected_package', 'position'] },
        { label: 'Bank Details', icon: Shield, fields: ['bank_name', 'account_number', 'ifsc_code', 'branch_name', 'account_holder_name', 'account_type'] },
        { label: 'Network & Payout', icon: TrendingUp, fields: ['selected_package', 'position', 'left_carry_forward_pv', 'right_carry_forward_pv', 'last_tree_left_pv', 'last_tree_right_pv'] },
        { label: 'Nominee', icon: UserCheck, fields: ['nominee_name', 'nominee_relation'] }
    ];

    const formatLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-gray-50/50">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-lg border border-blue-200 shadow-sm">
                            {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-900">{user.name}</h3>
                            <p className="text-sm text-gray-500">{user.email}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full text-gray-500 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto custom-scrollbar space-y-8">
                    {sections.map((section, idx) => (
                        <div key={idx} className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">{section.label}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                {section.fields.map(field => (
                                    <div key={field}>
                                        <p className="text-xs text-gray-400 font-medium mb-1">{formatLabel(field)}</p>
                                        <p className="text-sm font-semibold text-gray-900 break-words">
                                            {field === 'created_at' && user[field] ? new Date(user[field]).toLocaleString() :
                                                field.includes('pv') ? parseFloat(user[field] || 0).toFixed(2) :
                                                    (user[field] || <span className="text-gray-300 italic">Not set</span>)}
                                        </p>
                                    </div>
                                ))}
                                {section.label === 'Bank Details' && user.passbook_image && (
                                    <div className="md:col-span-2 lg:col-span-3">
                                        <p className="text-xs text-gray-400 font-medium mb-2">Passbook / Cancelled Cheque Image</p>
                                        <div className="relative group w-full max-w-md bg-gray-50 rounded-xl overflow-hidden border border-gray-200">
                                            {user.passbook_image.toLowerCase().endsWith('.pdf') ? (
                                                <div className="p-4 flex items-center gap-3">
                                                    <FileText className="text-red-500" size={32} />
                                                    <div>
                                                        <p className="text-sm font-bold font-mono">Passbook_Document.pdf</p>
                                                        <a href={user.passbook_image} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">View PDF Document</a>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <img src={user.passbook_image} alt="Passbook" className="w-full h-auto max-h-64 object-contain" />
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                                                        <a href={user.passbook_image} target="_blank" rel="noreferrer" className="p-3 bg-white rounded-full text-blue-600 hover:scale-110 transition-transform shadow-lg">
                                                            <Eye size={20} />
                                                        </a>
                                                        <a href={user.passbook_image} download className="p-3 bg-white rounded-full text-green-600 hover:scale-110 transition-transform shadow-lg">
                                                            <Download size={20} />
                                                        </a>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-between gap-3">
                    <button
                        onClick={() => {
                            onClose();
                            onUpdateCF(user);
                        }}
                        className="px-5 py-2.5 bg-indigo-50 text-indigo-700 font-bold rounded-xl hover:bg-indigo-100 transition-colors flex items-center gap-2"
                    >
                        <TrendingUp size={18} /> Update CF / PV
                    </button>
                    <button onClick={onClose} className="px-5 py-2.5 bg-gray-900 text-white font-medium rounded-xl hover:bg-black transition-colors shadow-lg">
                        Close Details
                    </button>
                </div>
            </div>
        </div>
    );
};

const Users = () => {
    const {
        users, fetchUsers, blockUser, unblockUser, deleteUser, addUser, updateUser,
        autoRefreshEnabled, toggleAutoRefresh, lastUpdated, isRefreshing, refreshAllData
    } = useAdmin();
    const { showAlert } = useAlert();
    const { showConfirm } = useConfirm();
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('All');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
    const [selectedUser, setSelectedUser] = useState(null);

    // View Modal State
    const [viewUser, setViewUser] = useState(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);

    // CF Update Modal State
    const [isCFModalOpen, setIsCFModalOpen] = useState(false);
    const [cfUser, setCfUser] = useState(null);

    const handleViewDetails = async (userId) => {
        try {
            const response = await apiService.getAdminUserDetails(userId);
            if (response.success) {
                setViewUser(response.data);
                setIsViewModalOpen(true);
            } else {
                showAlert(response.message || 'Failed to fetch user details', 'error');
            }
        } catch (err) {
            console.error(err);
            showAlert('Failed to fetch user details', 'error');
        }
    };

    const handleAddUser = () => {
        setModalMode('add');
        setSelectedUser(null);
        setIsModalOpen(true);
    };

    const handleEditUser = (user) => {
        setModalMode('edit');
        setSelectedUser(user);
        setIsModalOpen(true);
    };

    const handleSaveUser = async (data) => {
        try {
            if (modalMode === 'add') {
                await addUser(data);
                showAlert('User created successfully', 'success');
            } else {
                await updateUser(selectedUser.id, data);
                showAlert('User updated successfully', 'success');
            }
            setIsModalOpen(false);
        } catch (err) {
            showAlert(err.message || 'Failed to save user', 'error');
        }
    };

    const handleUpdateCF = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const data = {
            action: 'set_test_pv', // Using existing backend action
            user_id: cfUser.id,
            left_pv: formData.get('left_pv'),
            right_pv: formData.get('right_pv'),
            left_carry_forward_pv: formData.get('left_cf'),
            right_carry_forward_pv: formData.get('right_cf')
        };

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/admin/binary_payouts.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });
            const result = await response.json();
            if (result.success) {
                showAlert(result.message || 'Carry forward updated successfully', 'success');
                setIsCFModalOpen(false);
                refreshAllData(false);
            } else {
                showAlert(result.message || 'Failed to update CF', 'error');
            }
        } catch (err) {
            showAlert('Error updating carry forward', 'error');
        }
    };

    const getStatusColor = (status) => {
        const s = status ? status.toLowerCase() : '';
        switch (s) {
            case 'active': return 'bg-green-100 text-green-700 border-green-200';
            case 'inactive': return 'bg-gray-100 text-gray-700 border-gray-200';
            case 'blocked': return 'bg-red-100 text-red-700 border-red-200';
            case 'banned': return 'bg-red-100 text-red-700 border-red-200';
            case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    const handleBlock = async (userId) => {
        const confirmed = await showConfirm(
            'Block User',
            'Are you sure you want to block this user? They will not be able to access their account.',
            'danger'
        );
        if (confirmed) {
            blockUser(userId);
            showAlert('User blocked successfully', 'success');
        }
    };

    const handleUnblock = async (userId) => {
        const confirmed = await showConfirm(
            'Unblock User',
            'Do you want to unblock this user? They will regain access to their account.',
            'success'
        );
        if (confirmed) {
            unblockUser(userId);
            showAlert('User unblocked successfully', 'success');
        }
    };

    const handleDelete = async (userId) => {
        const confirmed = await showConfirm(
            'Delete User',
            'Are you sure you want to delete this user? This action cannot be undone and all user data will be permanently removed.',
            'danger'
        );
        if (confirmed) {
            deleteUser(userId);
            showAlert('User deleted successfully', 'success');
        }
    };

    const filteredUsers = users.filter(user => {
        const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterStatus === 'All' || user.status === filterStatus;
        return matchesSearch && matchesFilter;
    });

    const activeCount = users.filter(u => u.status.toLowerCase() === 'active').length;
    const blockedCount = users.filter(u => u.status.toLowerCase() === 'blocked').length;
    const inactiveCount = users.filter(u => ['inactive', 'pending'].includes(u.status.toLowerCase())).length;

    return (
        <Layout>
            <div className="space-y-6">
                {/* Page Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
                        <p className="text-gray-500 text-sm mt-1">View, manage and monitor all registered users</p>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        {/* Auto-Refresh Indicator */}
                        <AutoRefreshIndicator
                            lastUpdated={lastUpdated}
                            isAutoRefreshEnabled={autoRefreshEnabled}
                            onToggle={toggleAutoRefresh}
                            onRefresh={() => refreshAllData(false)}
                            loading={isRefreshing}
                            intervalSeconds={30}
                        />
                        <div className="flex gap-3">
                            <button className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium flex items-center gap-2 shadow-sm transition-all">
                                <Download size={18} /> Export
                            </button>
                            <button
                                onClick={handleAddUser}
                                className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium flex items-center gap-2 shadow-lg shadow-blue-200 transition-all"
                            >
                                <Shield size={18} /> Add User
                            </button>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider">Total Users</p>
                                <h3 className="text-2xl font-bold text-gray-900 mt-1">{users.length}</h3>
                            </div>
                            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                                <Shield size={20} />
                            </div>
                        </div>
                        <p className="text-green-600 text-xs font-medium mt-3 flex items-center gap-1">
                            <CheckCircle size={12} /> +125 this week
                        </p>
                    </div>
                    <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider">Active</p>
                                <h3 className="text-2xl font-bold text-gray-900 mt-1">{activeCount}</h3>
                            </div>
                            <div className="p-2 bg-green-50 text-green-600 rounded-lg">
                                <UserCheck size={20} />
                            </div>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-1.5 mt-3">
                            <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${(activeCount / users.length) * 100}%` }}></div>
                        </div>
                    </div>
                    <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider">Inactive</p>
                                <h3 className="text-2xl font-bold text-gray-900 mt-1">{inactiveCount}</h3>
                            </div>
                            <div className="p-2 bg-gray-100 text-gray-600 rounded-lg">
                                <UserX size={20} />
                            </div>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-1.5 mt-3">
                            <div className="bg-gray-400 h-1.5 rounded-full" style={{ width: `${(inactiveCount / users.length) * 100}%` }}></div>
                        </div>
                    </div>
                    <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider">Blocked</p>
                                <h3 className="text-2xl font-bold text-gray-900 mt-1">{blockedCount}</h3>
                            </div>
                            <div className="p-2 bg-red-50 text-red-600 rounded-lg">
                                <XCircle size={20} />
                            </div>
                        </div>
                        <p className="text-red-500 text-xs font-medium mt-3 flex items-center gap-1">
                            Requires Attention
                        </p>
                    </div>
                </div>

                {/* Filters & Search */}
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4 items-center">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search names, emails, IDs..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-medium text-sm"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                        <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-xl border border-gray-100 cursor-pointer" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                            <Calendar size={16} className="text-gray-400" />
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                className="bg-transparent text-xs font-bold focus:outline-none cursor-pointer"
                            />
                        </div>
                        <span className="text-gray-300 text-xs font-bold">to</span>
                        <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-xl border border-gray-100 cursor-pointer" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                            <Calendar size={16} className="text-gray-400" />
                            <input
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                className="bg-transparent text-xs font-bold focus:outline-none cursor-pointer"
                            />
                        </div>

                        <div className="h-8 w-[1px] bg-gray-200 hidden md:block"></div>

                        <div className="flex items-center gap-2">
                            <Filter size={18} className="text-gray-400" />
                            <select
                                className="bg-transparent border-none text-sm font-bold focus:ring-0 cursor-pointer text-gray-700"
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                            >
                                <option value="All">All Status</option>
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                                <option value="Blocked">Blocked</option>
                            </select>
                        </div>

                        <button
                            onClick={() => fetchUsers(false, { from: fromDate, to: toDate })}
                            className="px-4 py-2 bg-blue-600 text-white text-xs font-black uppercase tracking-widest rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-100 flex items-center gap-2"
                        >
                            <RefreshCw size={14} className={isRefreshing ? 'animate-spin' : ''} />
                            Filter
                        </button>
                    </div>
                </div>

                {/* Users Table / Mobile Cards */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    {/* Desktop View */}
                    <div className="hidden lg:block overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-100 text-gray-500 text-[10px] font-black uppercase tracking-[0.1em]">
                                    <th className="px-6 py-4">User Identity</th>
                                    <th className="px-6 py-4">Designation</th>
                                    <th className="px-6 py-4">Achievement</th>
                                    <th className="px-6 py-4">Lifecycle</th>
                                    <th className="px-6 py-4">Entry Date</th>
                                    <th className="px-6 py-4 text-right">Operations</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {filteredUsers.map((user) => (
                                    <tr key={user.id} className="hover:bg-indigo-50/30 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-black text-sm shadow-md">
                                                    {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-black text-gray-900 group-hover:text-blue-600 transition-colors">{user.name}</p>
                                                    <p className="text-[10px] font-bold text-gray-400 font-mono italic">{user.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-xs text-gray-700 font-black uppercase tracking-wider">{user.role}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            {user.rank !== 'None' && (
                                                <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-yellow-50 w-fit border border-yellow-100 shadow-sm">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-yellow-500"></div>
                                                    <span className="text-[10px] font-black text-yellow-700 uppercase italic">{user.rank}</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${getStatusColor(user.status)}`}>
                                                {user.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-xs font-bold text-gray-500 italic">
                                            {user.joinDate}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleViewDetails(user.id)}
                                                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                                    title="View Details"
                                                >
                                                    <Eye size={18} />
                                                </button>
                                                <button
                                                    onClick={() => handleEditUser(user)}
                                                    className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all" title="Edit User">
                                                    <Edit size={18} />
                                                </button>
                                                {['Blocked', 'Banned', 'blocked', 'banned'].includes(user.status) ? (
                                                    <button
                                                        onClick={() => handleUnblock(user.id)}
                                                        className="px-2 py-1 text-red-500 font-black text-[10px] border border-red-200 bg-red-50 rounded-lg hover:bg-red-100 transition-all uppercase tracking-widest italic"
                                                        title="Unblock User"
                                                    >
                                                        RESTORE
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => handleBlock(user.id)}
                                                        className="p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all"
                                                        title="Block User"
                                                    >
                                                        <Lock size={18} />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleDelete(user.id)}
                                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                                    title="Delete User"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile View - Cards */}
                    <div className="lg:hidden divide-y divide-gray-100">
                        {filteredUsers.map((user) => (
                            <div key={user.id} className="p-4 space-y-4 hover:bg-gray-50/50 transition-colors">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-black text-lg shadow-lg">
                                            {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                                        </div>
                                        <div>
                                            <p className="font-black text-gray-900 line-clamp-1">{user.name}</p>
                                            <p className="text-[10px] font-bold text-gray-400 italic font-mono truncate max-w-[150px]">{user.email}</p>
                                        </div>
                                    </div>
                                    <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-widest border ${getStatusColor(user.status)}`}>
                                        {user.status}
                                    </span>
                                </div>

                                <div className="grid grid-cols-2 gap-3 pb-2">
                                    <div className="bg-gray-50 p-2.5 rounded-xl border border-gray-100">
                                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1">Designation</span>
                                        <span className="text-xs font-black text-gray-700 italic uppercase">{user.role}</span>
                                    </div>
                                    <div className="bg-gray-50 p-2.5 rounded-xl border border-gray-100">
                                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1">Achievement</span>
                                        <span className="text-xs font-black text-orange-600 italic uppercase">{user.rank !== 'None' ? user.rank : 'NO RANK'}</span>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between pt-2">
                                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                        Joined: <span className="text-gray-600 font-black">{user.joinDate}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleViewDetails(user.id)}
                                            className="p-2.5 bg-blue-50 text-blue-600 rounded-xl shadow-sm border border-blue-100"
                                        >
                                            <Eye size={18} />
                                        </button>
                                        <button
                                            onClick={() => handleEditUser(user)}
                                            className="p-2.5 bg-green-50 text-green-600 rounded-xl shadow-sm border border-green-100"
                                        >
                                            <Edit size={18} />
                                        </button>
                                        {['Blocked', 'Banned', 'blocked', 'banned'].includes(user.status) ? (
                                            <button
                                                onClick={() => handleUnblock(user.id)}
                                                className="px-3 py-2 bg-red-50 text-red-600 text-[10px] font-black rounded-xl border border-red-100 uppercase tracking-widest"
                                            >
                                                RESTORE
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleBlock(user.id)}
                                                className="p-2.5 bg-orange-50 text-orange-600 rounded-xl shadow-sm border border-orange-100"
                                            >
                                                <Lock size={18} />
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleDelete(user.id)}
                                            className="p-2.5 bg-red-50 text-red-600 rounded-xl shadow-sm border border-red-100"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {filteredUsers.length === 0 && (
                    <div className="p-8 text-center text-gray-400 font-medium italic">
                        <UsersIcon size={32} className="mx-auto text-gray-100 mb-2" />
                        <p>No growth partners found matching your search.</p>
                    </div>
                )}

                <div className="p-4 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs font-black text-gray-400 uppercase tracking-widest bg-gray-50/20">
                    <span>Metrics: {filteredUsers.length} active / {users.length} total</span>
                    <div className="flex gap-2">
                        <button className="px-4 py-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50 shadow-sm transition-all active:scale-95 font-black">PREV</button>
                        <button className="px-4 py-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 shadow-sm transition-all active:scale-95 font-black">NEXT</button>
                    </div>
                </div>

                {/* Modal */}
                <UserModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    mode={modalMode}
                    user={selectedUser}
                    onSave={handleSaveUser}
                />

                {/* View Modal */}
                <ViewUserModal
                    isOpen={isViewModalOpen}
                    onClose={() => setIsViewModalOpen(false)}
                    user={viewUser}
                    onUpdateCF={(user) => {
                        setCfUser(user);
                        setIsCFModalOpen(true);
                    }}
                />

                {/* CF Update Modal */}
                {isCFModalOpen && cfUser && (
                    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center backdrop-blur-sm p-4">
                        <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
                            <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-indigo-50/50">
                                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                    <TrendingUp className="text-indigo-600" size={24} />
                                    Update CF & PV
                                </h3>
                                <button onClick={() => setIsCFModalOpen(false)} className="p-2 hover:bg-white rounded-full text-gray-400">
                                    <X size={20} />
                                </button>
                            </div>
                            <div className="p-6 bg-indigo-50/20">
                                <div className="flex items-center gap-3 mb-6 p-3 bg-white rounded-xl border border-indigo-100 shadow-sm">
                                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold">
                                        {cfUser.name.charAt(0)}
                                    </div>
                                    <div>
                                        <p className="font-bold text-gray-900">{cfUser.name}</p>
                                        <p className="text-xs text-gray-500">{cfUser.email}</p>
                                    </div>
                                </div>

                                <form onSubmit={handleUpdateCF} className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Left PV Baseline</label>
                                            <input
                                                type="number"
                                                name="left_pv"
                                                step="0.01"
                                                defaultValue={cfUser.last_tree_left_pv || 0}
                                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all shadow-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Right PV Baseline</label>
                                            <input
                                                type="number"
                                                name="right_pv"
                                                step="0.01"
                                                defaultValue={cfUser.last_tree_right_pv || 0}
                                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all shadow-sm"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-indigo-400 uppercase tracking-widest mb-1.5 ml-1">Left Carry Forward</label>
                                            <input
                                                type="number"
                                                name="left_cf"
                                                step="0.01"
                                                defaultValue={cfUser.left_carry_forward_pv || 0}
                                                className="w-full px-4 py-3 border border-indigo-200 bg-white rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all shadow-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-purple-400 uppercase tracking-widest mb-1.5 ml-1">Right Carry Forward</label>
                                            <input
                                                type="number"
                                                name="right_cf"
                                                step="0.01"
                                                defaultValue={cfUser.right_carry_forward_pv || 0}
                                                className="w-full px-4 py-3 border border-purple-200 bg-white rounded-xl focus:ring-2 focus:ring-purple-500 outline-none transition-all shadow-sm"
                                            />
                                        </div>
                                    </div>

                                    <div className="pt-4 flex gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setIsCFModalOpen(false)}
                                            className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-50 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="flex-1 px-4 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 shadow-lg shadow-indigo-100 transition-all flex items-center justify-center gap-2"
                                        >
                                            <RefreshCw size={18} /> Update Data
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default Users;
